import * as THREE from 'three';

import { useEffect, useRef, useState } from 'react';

import { Billboard } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';

// Define types for props
type SimpleChickenProps = {
  npcId: string;
  position: [number, number, number];
  facingDirection: [number, number, number];
  health: number;
  behaviorState: 'patrolling' | 'fleeing' | 'dead';
};

// Create a particle system for hit effects
const createParticleSystem = () => {
  const particleCount = 20;
  const particles = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 0.5;
    positions[i * 3 + 1] = Math.random() * 0.5;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
  }

  particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const particleMaterial = new THREE.PointsMaterial({
    color: 0xff0000,
    size: 0.05,
    transparent: true,
    opacity: 0.8,
  });

  return new THREE.Points(particles, particleMaterial);
};

// SimpleChicken component in low-poly style similar to other characters
export function SimpleChicken({
  position,
  facingDirection,
  health,
  behaviorState,
}: SimpleChickenProps) {
  const group = useRef<THREE.Group>(null);
  const bodyRef = useRef<THREE.Mesh>(null);
  const headRef = useRef<THREE.Mesh>(null);
  const leftWingRef = useRef<THREE.Mesh>(null);
  const rightWingRef = useRef<THREE.Mesh>(null);
  const leftLegRef = useRef<THREE.Mesh>(null);
  const rightLegRef = useRef<THREE.Mesh>(null);

  const [showHitEffect, setShowHitEffect] = useState(false);
  const [lastHealth, setLastHealth] = useState(health);
  const hitEffectRef = useRef<THREE.Points>();

  // Animation state
  const bodyBobRef = useRef(0);
  const headBobRef = useRef(0);
  const wingFlapRef = useRef(0);
  const peckingRef = useRef(0);
  const isPeckingRef = useRef(false);

  // Initialize hit effect
  useEffect(() => {
    if (!hitEffectRef.current) {
      hitEffectRef.current = createParticleSystem();
    }

    // Clean up the hit effect
    return () => {
      if (hitEffectRef.current) {
        hitEffectRef.current.geometry.dispose();
        (hitEffectRef.current.material as THREE.Material).dispose();
      }
    };
  }, []);

  // Handle health changes and show hit effect
  useEffect(() => {
    if (health < lastHealth) {
      setShowHitEffect(true);
      setTimeout(() => setShowHitEffect(false), 200);
    }
    setLastHealth(health);
  }, [health, lastHealth]);

  // Handle animation and movement
  useFrame((_, delta) => {
    if (!group.current) return;

    // Position the chicken
    group.current.position.set(position[0], position[1], position[2]);

    // Calculate chicken rotation based on facing direction
    if (
      facingDirection &&
      (facingDirection[0] !== 0 || facingDirection[2] !== 0)
    ) {
      const angle = Math.atan2(facingDirection[0], facingDirection[2]);
      group.current.rotation.y = angle;
    }

    // Handle animation based on behavior state
    if (behaviorState === 'dead') {
      // When dead, lay on side
      if (bodyRef.current) {
        bodyRef.current.rotation.z = Math.PI / 2;
      }
      if (headRef.current) {
        headRef.current.rotation.z = Math.PI / 2;
      }
      return;
    }

    // Determine if we should be pecking (only when patrolling and not moving much)
    if (behaviorState === 'patrolling' && Math.random() < 0.005) {
      isPeckingRef.current = !isPeckingRef.current;
    }

    // Reset pecking when fleeing
    if (behaviorState === 'fleeing') {
      isPeckingRef.current = false;
    }

    // Body bob animation (different for patrolling vs fleeing)
    const bobSpeed = behaviorState === 'fleeing' ? 12 : 5;
    bodyBobRef.current += delta * bobSpeed;
    const bodyBobAmount = behaviorState === 'fleeing' ? 0.15 : 0.08;

    if (bodyRef.current) {
      bodyRef.current.position.y =
        Math.sin(bodyBobRef.current) * bodyBobAmount + 0.3;
      bodyRef.current.rotation.z = 0; // Reset from dead state
    }

    // Head animation
    headBobRef.current += delta * 4;
    if (headRef.current) {
      headRef.current.position.y = Math.sin(headBobRef.current) * 0.03 + 0.75;
      headRef.current.rotation.z = 0; // Reset from dead state

      // Pecking animation
      if (isPeckingRef.current) {
        peckingRef.current += delta * 8;
        // Forward and downward motion for pecking
        headRef.current.rotation.x = Math.sin(peckingRef.current) * 0.8 - 0.2;
      } else {
        // Reset back to normal position gradually
        headRef.current.rotation.x *= 0.9;
      }
    }

    // Wing flapping animation (faster when fleeing)
    const wingSpeed = behaviorState === 'fleeing' ? 15 : 4;
    wingFlapRef.current += delta * wingSpeed;

    if (leftWingRef.current && rightWingRef.current) {
      // Wings flap more vigorously when fleeing
      const flapAmount = behaviorState === 'fleeing' ? 0.8 : 0.2;
      leftWingRef.current.rotation.z =
        Math.sin(wingFlapRef.current) * flapAmount + 0.2;
      rightWingRef.current.rotation.z =
        -Math.sin(wingFlapRef.current) * flapAmount - 0.2;
    }

    // Leg animation for walking/running
    if (leftLegRef.current && rightLegRef.current) {
      const legSpeed = behaviorState === 'fleeing' ? 12 : 6;
      leftLegRef.current.rotation.x = Math.sin(bodyBobRef.current) * 0.4;
      rightLegRef.current.rotation.x =
        Math.sin(bodyBobRef.current + Math.PI) * 0.4;
    }
  });

  // Determine color based on behavior state
  let bodyColor = '#E8D09C'; // Default light tan color for body
  let headColor = '#E74C3C'; // Red for comb/wattle
  let legColor = '#E67E22'; // Orange for legs

  // Show red flash when hit
  if (showHitEffect) {
    bodyColor = '#FF0000';
    headColor = '#FF0000';
    legColor = '#FF0000';
  } else if (behaviorState === 'dead') {
    // Gray when dead
    bodyColor = '#95A5A6';
    headColor = '#7F8C8D';
    legColor = '#7F8C8D';
  } else if (behaviorState === 'fleeing') {
    // Slightly lighter colors when fleeing to indicate distress
    bodyColor = '#F5E6BD';
  }

  return (
    <group ref={group}>
      {/* Hit effect particles */}
      {showHitEffect && hitEffectRef.current && (
        <primitive object={hitEffectRef.current} position={[0, 0.5, 0]} />
      )}

      {/* Health bar - only show when damaged */}
      {health < 30 && behaviorState !== 'dead' && (
        <Billboard position={[0, 1.0, 0]}>
          <mesh>
            <planeGeometry args={[0.5, 0.1]} />
            <meshBasicMaterial color="#333333" />
          </mesh>
          <mesh position={[-0.25 + (health / 30) * 0.25, 0, 0.01]}>
            <planeGeometry args={[(health / 30) * 0.5, 0.08]} />
            <meshBasicMaterial color="#00FF00" />
          </mesh>
        </Billboard>
      )}

      {/* Chicken body */}
      <mesh ref={bodyRef} position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.4, 0.3, 0.6]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>

      {/* Head with comb */}
      <mesh ref={headRef} position={[0, 0.75, 0.25]} castShadow>
        <boxGeometry args={[0.25, 0.25, 0.25]} />
        <meshStandardMaterial color={bodyColor} />

        {/* Comb/wattle */}
        <mesh position={[0, 0.1, 0]} castShadow>
          <boxGeometry args={[0.1, 0.1, 0.05]} />
          <meshStandardMaterial color={headColor} />
        </mesh>

        {/* Beak */}
        <mesh position={[0, 0, 0.15]} castShadow>
          <boxGeometry args={[0.1, 0.1, 0.1]} />
          <meshStandardMaterial color="#F39C12" />
        </mesh>
      </mesh>

      {/* Wings */}
      <mesh ref={leftWingRef} position={[-0.25, 0.3, 0]} castShadow>
        <boxGeometry args={[0.1, 0.2, 0.5]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>

      <mesh ref={rightWingRef} position={[0.25, 0.3, 0]} castShadow>
        <boxGeometry args={[0.1, 0.2, 0.5]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>

      {/* Legs */}
      <mesh ref={leftLegRef} position={[-0.15, 0.15, -0.2]} castShadow>
        <boxGeometry args={[0.05, 0.3, 0.05]} />
        <meshStandardMaterial color={legColor} />
      </mesh>

      <mesh ref={rightLegRef} position={[0.15, 0.15, -0.2]} castShadow>
        <boxGeometry args={[0.05, 0.3, 0.05]} />
        <meshStandardMaterial color={legColor} />
      </mesh>
    </group>
  );
}
